<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { formatBytes } from '@/utils'
import { AdminS3Bucket } from '@/types/admin'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminStorageBuckets' })

const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const buckets = ref<AdminS3Bucket[]>([])
const searchQuery = ref('')
const actionId = ref<number | null>(null)

const regionLabels: Record<string, string> = {
  'eu-central-1': 'dashboard.storage.buckets.regions.eu-central-1',
  'eu-central-2': 'dashboard.storage.buckets.regions.eu-central-2',
  'eu-north-1': 'dashboard.storage.buckets.regions.eu-north-1'
}

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString()
}

async function fetchBuckets() {
  loading.value = true
  try {
    const res = await useAxios<null, AdminS3Bucket[]>('GET', '/admin/storage/buckets', true)
    if (res && res.status === STATUS_CODES.OK && res.data) {
      const parsed = AdminS3Bucket.array().safeParse(res.data)
      if (parsed.success) {
        buckets.value = parsed.data
      } else {
        console.warn('[AdminStorageBuckets] Zod parse error:', parsed.error)
      }
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminStorageBuckets' })
  } finally {
    loading.value = false
  }
}

const filteredBuckets = computed(() => {
  if (!searchQuery.value.trim()) return buckets.value
  const query = searchQuery.value.toLowerCase()
  return buckets.value.filter(b =>
    b.name.toLowerCase().includes(query) ||
    String(b.account_id).includes(query)
  )
})

async function suspendBucket(bucket: AdminS3Bucket) {
  actionId.value = bucket.id
  try {
    const res = await useAxios<null, null>('POST', `/admin/storage/buckets/${bucket.id}/suspend`, true)
    if (res && res.status === STATUS_CODES.NO_CONTENT) {
      bucket.status = 'suspended'
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.storage.actions.suspendSuccess'),
        message: bucket.name
      })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminStorageBuckets:suspend' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.admin.storage.actions.error'),
      message: bucket.name
    })
  } finally {
    actionId.value = null
  }
}

async function unsuspendBucket(bucket: AdminS3Bucket) {
  actionId.value = bucket.id
  try {
    const res = await useAxios<null, null>('POST', `/admin/storage/buckets/${bucket.id}/unsuspend`, true)
    if (res && res.status === STATUS_CODES.NO_CONTENT) {
      bucket.status = 'active'
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.storage.actions.unsuspendSuccess'),
        message: bucket.name
      })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminStorageBuckets:unsuspend' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.admin.storage.actions.error'),
      message: bucket.name
    })
  } finally {
    actionId.value = null
  }
}

onMounted(() => {
  fetchBuckets()
})
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.storage.title')"
    :subtitle="t('dashboard.admin.storage.subtitle')"
    icon="mdi-database-outline"
    icon-color="blue"
  >
    <template #actions>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchBuckets"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Search Bar -->
    <div class="search-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="t('dashboard.admin.storage.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredBuckets.length === 0" class="empty-state">
      <v-icon icon="mdi-database-off-outline" size="64" />
      <h3>{{ t('dashboard.admin.storage.empty') }}</h3>
      <p>{{ t('dashboard.admin.storage.emptyDescription') }}</p>
    </div>

    <!-- Buckets Table -->
    <div v-else class="table-container">
      <table class="buckets-table">
        <thead>
          <tr>
            <th>{{ t('dashboard.admin.storage.columns.name') }}</th>
            <th>{{ t('dashboard.admin.storage.columns.region') }}</th>
            <th>{{ t('dashboard.admin.storage.columns.account') }}</th>
            <th>{{ t('dashboard.admin.storage.columns.status') }}</th>
            <th>{{ t('dashboard.admin.storage.columns.public') }}</th>
            <th>{{ t('dashboard.admin.storage.columns.storage') }}</th>
            <th>{{ t('dashboard.admin.storage.columns.objects') }}</th>
            <th>{{ t('dashboard.admin.storage.columns.created') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="bucket in filteredBuckets"
            :key="bucket.id"
            class="table-row"
          >
            <td class="name-cell">
              <span class="bucket-name">{{ bucket.name }}</span>
            </td>
            <td class="region-cell">
              <span class="region-chip">{{ t(regionLabels[bucket.region] || bucket.region) }}</span>
            </td>
            <td class="account-cell">{{ bucket.account_id }}</td>
            <td class="status-cell">
              <span class="status-badge" :class="bucket.status">{{ bucket.status }}</span>
            </td>
            <td class="public-cell">
              <span class="public-badge" :class="{ public: bucket.public }">
                {{ bucket.public ? t('dashboard.admin.storage.publicYes') : t('dashboard.admin.storage.publicNo') }}
              </span>
            </td>
            <td class="storage-cell mono">{{ formatBytes(bucket.storage_bytes) }}</td>
            <td class="objects-cell mono">{{ bucket.object_count.toLocaleString() }}</td>
            <td class="date-cell">{{ formatDate(bucket.created) }}</td>
            <td class="action-cell">
              <v-btn
                v-if="bucket.status === 'active'"
                color="warning"
                variant="tonal"
                size="small"
                :loading="actionId === bucket.id"
                @click="suspendBucket(bucket)"
              >
                <v-icon icon="mdi-lock" class="me-1" size="16" />
                {{ t('dashboard.admin.storage.actions.suspend') }}
              </v-btn>
              <v-btn
                v-else-if="bucket.status === 'suspended'"
                color="success"
                variant="tonal"
                size="small"
                :loading="actionId === bucket.id"
                @click="unsuspendBucket(bucket)"
              >
                <v-icon icon="mdi-lock-open" class="me-1" size="16" />
                {{ t('dashboard.admin.storage.actions.unsuspend') }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.search-bar {
  margin-bottom: 24px;
}

.search-input {
  max-width: 400px;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(120, 163, 255, 0.3);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0;
  }
}

.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.buckets-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.name-cell .bucket-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.region-chip {
  display: inline-flex;
  padding: 3px 10px;
  background: rgba(0, 176, 255, 0.1);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #64b5f6;
}

.account-cell,
.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.mono {
  font-family: monospace;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.active {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }

  &.suspended {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.deleted {
    background: rgba(244, 67, 54, 0.15);
    color: #ef5350;
  }
}

.public-badge {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  background: rgba(120, 163, 255, 0.08);
  color: rgba(200, 218, 255, 0.5);

  &.public {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }
}

.action-cell {
  text-align: right;
}

/* Light Theme */
.v-theme--lightTheme {
  .table-container {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .buckets-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .name-cell .bucket-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .account-cell,
  .date-cell {
    color: rgba(15, 23, 42, 0.6);
  }
}

@media (max-width: 768px) {
  .search-input {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .buckets-table {
    min-width: 800px;
  }
}
</style>
